import { useState, type ReactNode } from 'react'
import { getErrorMessage } from '@features/shared/api/client'
import { DropdownMenu, type MenuSection } from '@features/shared/components/DropdownMenu'
import FadeTruncatedText from '@features/shared/components/FadeTruncatedText'
import { IconButton } from '@features/shared/components/IconButton'
import { POPOVER_MENU_ICON_SIZE_PX } from '@features/shared/components/PopoverMenu'
import {
  ChevronDownIcon,
  EditIcon,
  FolderIcon,
  KebabIcon,
  TrashIcon,
} from '@features/shared/icons/icons'
import {
  MENU_ICON_SLOT_CLASS,
  MENU_ICON_SLOTS_CLASS,
  menuIconSlotClass,
  menuIconSlotsLeadClass,
} from '@features/sidebar/components/menuIconSlots'
import { CHAT_FOLDER_NAME_MAX_LENGTH } from '../api/contracts'
import {
  NAME_INPUT_CLASS,
  ROW_CLASS,
  ROW_STATE_CLASS,
  SIDEBAR_DROPDOWN_Z_INDEX,
} from './chatHistoryRowStyles'

/** Runs a folder action, keeping its busy state and error for the row. */
function useFolderAction() {
  const [isBusy, setIsBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const run = async (action: () => Promise<unknown>, failure: string) => {
    setIsBusy(true)
    setError(null)
    try {
      await action()
      return true
    } catch (err) {
      setError(getErrorMessage(err, failure))
      return false
    } finally {
      setIsBusy(false)
    }
  }

  return { isBusy, error, setError, run }
}

interface FolderNameInputProps {
  initialName: string
  isBusy: boolean
  error: string | null
  /** A trimmed, non-blank name; an unchanged or blank one cancels instead. */
  onSubmit: (name: string) => void
  onCancel: () => void
}

function FolderNameInput({ initialName, isBusy, error, onSubmit, onCancel }: FolderNameInputProps) {
  const [draft, setDraft] = useState(initialName)

  const submit = () => {
    if (isBusy) return
    const next = draft.trim()
    if (!next || next === initialName) onCancel()
    else onSubmit(next)
  }

  return (
    <div className="px-1 py-1">
      <input
        autoFocus
        type="text"
        aria-label="Folder name"
        value={draft}
        maxLength={CHAT_FOLDER_NAME_MAX_LENGTH}
        disabled={isBusy}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') submit()
          if (event.key === 'Escape') {
            event.preventDefault()
            event.stopPropagation()
            onCancel()
          }
        }}
        onBlur={submit}
        className={NAME_INPUT_CLASS}
      />
      {error && <p className="px-1 pt-1 text-xs text-danger">{error}</p>}
    </div>
  )
}

/** The drawer's New folder row: a name box that creates the folder on Enter. */
export function NewChatFolderRow({ onCreate }: { onCreate: (name: string) => Promise<unknown> }) {
  const [isNaming, setIsNaming] = useState(false)
  const { isBusy, error, setError, run } = useFolderAction()

  if (isNaming) {
    return (
      <FolderNameInput
        initialName=""
        isBusy={isBusy}
        error={error}
        onSubmit={(name) => {
          void run(() => onCreate(name), "This folder couldn't be created.").then((created) => {
            if (created) setIsNaming(false)
          })
        }}
        onCancel={() => setIsNaming(false)}
      />
    )
  }

  return (
    <button
      type="button"
      onClick={() => {
        setError(null)
        setIsNaming(true)
      }}
      className={`${ROW_CLASS} gap-2 font-medium ${ROW_STATE_CLASS.idle}`}
    >
      <FolderIcon size={14} className="shrink-0" aria-hidden="true" />
      New folder
    </button>
  )
}

interface ChatFolderSectionProps {
  name: string
  threadCount: number
  /** The folder's conversations are still loading, or couldn't be. */
  isLoading?: boolean
  isError?: boolean
  onRename: (name: string) => Promise<unknown>
  onDelete: () => Promise<unknown>
  /** The folder's conversation rows. */
  children: ReactNode
}

/**
 * A folder in the AI Assistant's drawer: its name (click to show or hide its
 * conversations, open at first), a count, and a menu to rename or delete it.
 * Deleting a folder moves its conversations out of it; it never deletes them.
 */
export default function ChatFolderSection({
  name,
  threadCount,
  isLoading = false,
  isError = false,
  onRename,
  onDelete,
  children,
}: ChatFolderSectionProps) {
  const [mode, setMode] = useState<'view' | 'rename' | 'confirm-delete'>('view')
  const [isExpanded, setIsExpanded] = useState(true)
  const [menuOpen, setMenuOpen] = useState(false)
  const { isBusy, error, setError, run } = useFolderAction()

  const menuSections: MenuSection[] = [
    {
      items: [
        {
          type: 'action',
          id: 'rename-chat-folder',
          label: 'Rename',
          icon: <EditIcon size={POPOVER_MENU_ICON_SIZE_PX} aria-hidden="true" />,
          onClick: () => {
            setError(null)
            setMode('rename')
          },
        },
        {
          type: 'action',
          id: 'delete-chat-folder',
          label: 'Delete',
          tone: 'danger',
          icon: <TrashIcon size={POPOVER_MENU_ICON_SIZE_PX} aria-hidden="true" />,
          onClick: () => {
            setError(null)
            setMode('confirm-delete')
          },
        },
      ],
    },
  ]

  let header: ReactNode
  if (mode === 'rename') {
    header = (
      <FolderNameInput
        initialName={name}
        isBusy={isBusy}
        error={error}
        onSubmit={(next) => {
          void run(() => onRename(next), "This folder couldn't be renamed.").then((renamed) => {
            if (renamed) setMode('view')
          })
        }}
        onCancel={() => setMode('view')}
      />
    )
  } else if (mode === 'confirm-delete') {
    // Confirmed inline rather than in a modal, like a conversation: an
    // unpinned drawer unmounts its content as soon as the pointer leaves it.
    header = (
      <div className="rounded-md px-2 py-2 text-sm ring-1 ring-[color:var(--sidebar-submenu-border)]">
        <p className="text-[color:var(--sidebar-submenu-text-data)]">
          Delete the folder{' '}
          <span className="font-medium text-[color:var(--sidebar-submenu-text-default)]">
            {name}
          </span>
          ? Its conversations move out of it and aren't deleted.
        </p>
        {error && <p className="pt-1 text-xs text-danger">{error}</p>}
        <div className="mt-2 flex justify-end gap-2">
          <button
            type="button"
            onClick={() => setMode('view')}
            disabled={isBusy}
            className="rounded-md px-2 py-1 text-xs font-medium text-[color:var(--sidebar-submenu-text-data)] hover:bg-[color:var(--sidebar-item-hover-bg)] disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void run(onDelete, "This folder couldn't be deleted.")}
            disabled={isBusy}
            className="rounded-md bg-[color:var(--action-danger-bg)] px-2 py-1 text-xs font-medium text-[color:var(--action-danger-text)] hover:bg-[color:var(--action-danger-bg-hover)] disabled:opacity-60"
          >
            {isBusy ? 'Deleting...' : 'Delete folder'}
          </button>
        </div>
      </div>
    )
  } else {
    header = (
      <div className={`${ROW_CLASS} ${ROW_STATE_CLASS.idle}`}>
        <button
          type="button"
          aria-expanded={isExpanded}
          onClick={() => setIsExpanded((expanded) => !expanded)}
          // The button's overlay covers the row, so the whole row toggles.
          className="flex min-w-0 flex-1 items-center gap-1.5 self-stretch text-left after:absolute after:inset-0 after:rounded-md after:content-['']"
        >
          <ChevronDownIcon
            size={10}
            aria-hidden="true"
            className={`shrink-0 text-[color:var(--sidebar-submenu-text-faint)] transition-transform ${
              isExpanded ? '' : '-rotate-90'
            }`}
          />
          <FolderIcon size={14} aria-hidden="true" className="shrink-0" />
          <FadeTruncatedText>{name}</FadeTruncatedText>
          {threadCount > 0 && (
            <span className="shrink-0 text-[10px] text-[color:var(--sidebar-submenu-text-faint)]">
              {threadCount}
            </span>
          )}
        </button>
        <span
          className={`pointer-events-none relative z-10 ml-auto h-6 ${menuIconSlotsLeadClass(
            menuOpen
          )} ${MENU_ICON_SLOTS_CLASS}`}
        >
          <span className={menuIconSlotClass(menuOpen ? 'shown' : 'collapsed')}>
            <DropdownMenu
              open={menuOpen}
              onOpenChange={setMenuOpen}
              sections={menuSections}
              align="right"
              placement="right-start"
              sideOffsetPx={6}
              className="min-w-40"
              portalZIndex={SIDEBAR_DROPDOWN_Z_INDEX}
              trigger={
                <IconButton
                  label="Folder options"
                  tooltip={menuOpen ? null : undefined}
                  className={`pointer-events-auto ${MENU_ICON_SLOT_CLASS} text-[color:var(--sidebar-submenu-text-faintest)] transition-colors hover:text-[color:var(--sidebar-submenu-text-data)]`}
                >
                  <KebabIcon size={14} aria-hidden="true" />
                </IconButton>
              }
            />
          </span>
        </span>
      </div>
    )
  }

  return (
    <div role="group" aria-label={name} className="flex flex-col gap-0.5">
      {header}
      {isExpanded && (
        <div className="relative flex flex-col gap-0.5 pl-4">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute bottom-1 left-[13px] top-0 w-px bg-[color:var(--sidebar-submenu-border)]"
          />
          {isLoading ? null : isError ? (
            <p className="px-2 py-1 text-xs text-danger">
              This folder's conversations couldn't be loaded.
            </p>
          ) : threadCount === 0 ? (
            <p className="px-2 py-1 text-xs text-[color:var(--sidebar-submenu-text-muted)]">
              No conversations in this folder.
            </p>
          ) : (
            children
          )}
        </div>
      )}
    </div>
  )
}
